import {useAuthStore} from '@/stores/authStore';
import {mapTiles} from '@/composables/mapConfig';

// App fallback coordinate (central US). Mirrors the API's
// config('services.default_location').
const DEFAULT_LAT = 38.736040;
const DEFAULT_LNG = -100.274368;

function addressQuery(model) {
    if (!model || typeof model !== 'object') return null;
    const parts = [model.addr_1, model.addr_city, model.addr_state, model.addr_postalcode, model.addr_country]
        .map(v => (v == null ? '' : String(v).trim()))
        .filter(s => s.length > 0);
    return parts.length ? parts.join(', ') : null;
}

function validCoord(lat, lng) {
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return false;
    // Null island: what a failed geocode and an uninitialised value look like.
    if (Math.abs(lat) < 0.0001 && Math.abs(lng) < 0.0001) return false;
    return true;
}

// Keyless Nominatim forward geocode, done in the BROWSER (never the API server,
// which geocoding a single IP for every activation would get banned). Time-boxed
// and never throws.
async function forwardGeocode(query) {
    const q = (query || '').trim();
    if (!q) return null;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 5000);
    try {
        const res = await fetch(
            `${mapTiles.searchUrl}?format=json&limit=1&q=${encodeURIComponent(q)}`,
            {signal: controller.signal},
        );
        if (!res.ok) return null;
        const data = await res.json();
        if (!Array.isArray(data) || data.length === 0) return null;
        const lat = parseFloat(data[0].lat);
        const lng = parseFloat(data[0].lon);
        return validCoord(lat, lng) ? {lat, lng} : null;
    } catch (e) {
        return null;
    } finally {
        clearTimeout(timer);
    }
}

/**
 * Resolve a freshly activated device's first map position, client-side:
 * company address -> user address -> app default. Always resolves (never
 * throws); worst case it returns the default coordinate.
 */
export async function resolveActivationCoords() {
    const auth = useAuthStore();
    const user = auth?.auth?.user || null;
    const company = user?.company || null;

    for (const q of [addressQuery(company), addressQuery(user)]) {
        if (!q) continue;
        const coords = await forwardGeocode(q);
        if (coords) return coords;
    }

    return {lat: DEFAULT_LAT, lng: DEFAULT_LNG};
}
